import { defineComponent, reactive, provide, computed, inject, markRaw, shallowRef, ref, watch, onBeforeUnmount, createBlock, createCommentVNode, openBlock, withCtx, createElementVNode, normalizeStyle, normalizeClass, createElementBlock, renderSlot, createTextVNode, toDisplayString, unref, withDirectives, createVNode, resolveDynamicComponent, normalizeProps, mergeProps, vShow, TransitionGroup, Fragment, renderList } from "vue";
import { useI18n } from "vue-i18n";
import UModal from "@nuxt/ui/components/Modal.vue";
import UButton from "@nuxt/ui/components/Button.vue";
import { Z_SHEET_ELEVATED } from "app-theme";
import { useIsMobile } from "./index110.js";
import { SHEET_LAYER_NESTED, SHEET_LAYER_GUARD, SHEET_ELEVATED, SHEET_LEADING_CONTROL_CLASS } from "./index48.js";
const _hoisted_1 = {
  key: 0,
  class: "flex items-center gap-1.5 px-4 min-h-15 shrink-0"
};
const _hoisted_2 = { class: "text-highlighted text-panel-title flex-1 truncate" };
const _hoisted_3 = { class: "cod-sheet-viewport" };
const ROOT_LAYER = 0;
const LEAVE_FALLBACK_MS = 1e3;
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "CodSheet",
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    /** Root step component of the step stack. Omit to use the default slot. */
    root: {
      type: Object,
      default: null
    },
    rootProps: {
      type: Object,
      default: () => ({})
    },
    /** canDismiss parity: when true, backdrop/ESC/back cannot dismiss. */
    guard: {
      type: [Boolean, Function],
      default: false
    },
    /** Header-X override. Default: shown unless guarded. Set true to keep the
        X on a guarded sheet (X closes programmatically, which guard allows). */
    closable: {
      type: Boolean,
      default: void 0
    },
    /** Desktop panel width (any CSS length). */
    width: {
      type: String,
      default: "480px"
    },
    /** Desktop panel height ('auto' hugs content; e.g. '90dvh' for tall forms). */
    height: {
      type: String,
      default: "auto"
    },
    /** Full-screen on mobile (the bottom-up sheet presentation). */
    fullscreenMobile: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: false
    },
    /** The host shows a deeper view in place of its first one (not a pushed step — e.g. a
        picker's second view). The header then leads with ← as on a pushed step, and that ← and
        the hardware back emit `back` for the host to return to its first view. */
    inPlaceStep: {
      type: Boolean,
      default: false
    },
    /** §4.5 footer layout (actions right). Disable for custom footer content. */
    footerChrome: {
      type: Boolean,
      default: true
    },
    /** Stack above standard modals (z-less — they stack by DOM order).
        Needed by surfaces that open FROM a modal (confirms): an app-boot-
        mounted sheet's teleport node sits BEFORE later-opened modals in
        <body>, so DOM order alone renders it underneath. Floating panels
        (menus, popovers) clear this layer via the theme's Z_FLOATING. A sheet
        opened inside an elevated sheet inherits the layer. */
    elevated: {
      type: Boolean,
      default: false
    },
    /** Open with the focus on the sheet itself, not its first button (Tab still reaches the
        buttons) — for a question whose every button is an answer, so an Enter (a terminal's OK
        key, a scanner) picks none of them. */
    focusSheetOnOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "didDismiss", "close", "back"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const guardedLayers = reactive(/* @__PURE__ */ new Map());
    const SheetLayer = defineComponent({
      props: {
        nested: { type: Boolean, default: false },
        layer: { type: Number, required: true }
      },
      setup(layerProps, { slots }) {
        provide(SHEET_LAYER_NESTED, layerProps.nested);
        provide(SHEET_LAYER_GUARD, (guarded) => {
          const forms = (guardedLayers.get(layerProps.layer) ?? 0) + (guarded ? 1 : -1);
          if (forms > 0) guardedLayers.set(layerProps.layer, forms);
          else guardedLayers.delete(layerProps.layer);
        });
        return () => {
          var _a;
          return (_a = slots.default) == null ? void 0 : _a.call(slots);
        };
      }
    });
    const { t } = useI18n();
    const mobile = useIsMobile();
    const open = computed({
      get: () => props.modelValue,
      set: (v) => emit("update:modelValue", v)
    });
    const hostGuarded = computed(() => typeof props.guard === "function" ? props.guard() : props.guard);
    const isGuarded = computed(() => hostGuarded.value || guardedLayers.size > 0);
    const showClose = computed(() => props.closable ?? !hostGuarded.value);
    const fullscreenActive = computed(() => props.fullscreenMobile && mobile.value);
    const elevated = props.elevated || inject(SHEET_ELEVATED, false);
    provide(SHEET_ELEVATED, elevated);
    const rootComponent = computed(() => props.root ? markRaw(props.root) : null);
    const steps = shallowRef([]);
    const direction = ref("forward");
    const depth = computed(() => steps.value.length);
    const stepAnimating = ref(false);
    let stepId = 0;
    function layerShown(level) {
      return level === depth.value || stepAnimating.value && direction.value === "forward" && level === depth.value - 1;
    }
    function navigate(component, params = {}) {
      direction.value = "forward";
      stepAnimating.value = true;
      steps.value = [...steps.value, { id: ++stepId, component: markRaw(component), props: params }];
    }
    function goBack() {
      if (!steps.value.length) return;
      direction.value = "back";
      stepAnimating.value = true;
      steps.value = steps.value.slice(0, -1);
    }
    const leadsBack = computed(() => depth.value > 0 || props.inPlaceStep);
    function stepBack() {
      if (steps.value.length) goBack();
      else emit("back");
    }
    function close(...args) {
      open.value = false;
      emit("close", ...args);
    }
    const contentHandlers = {
      onInteractOutside(event) {
        var _a, _b;
        const target = (_b = (_a = event.detail) == null ? void 0 : _a.originalEvent) == null ? void 0 : _b.target;
        const onBackdrop = target instanceof Element && !!target.closest('[data-slot="overlay"]');
        if (onBackdrop && !isGuarded.value) close();
      },
      onEscapeKeyDown() {
        if (!isGuarded.value) close();
      },
      // reka focuses the first tabbable element unless this is prevented; the event's target is the
      // dialog content (tabindex -1), which then takes the focus instead.
      onOpenAutoFocus(event) {
        if (!props.focusSheetOnOpen) return;
        event.preventDefault();
        const sheet = event.target;
        sheet.focus();
      }
    };
    provide("navigate", navigate);
    provide("goBack", goBack);
    provide("closeModal", close);
    __expose({ navigate, goBack, close });
    const mounted = ref(true);
    let leaveFallback;
    watch(() => props.modelValue, (isOpen) => {
      clearTimeout(leaveFallback);
      if (isOpen) {
        mounted.value = true;
      } else {
        leaveFallback = setTimeout(() => {
          mounted.value = false;
          onAfterLeave();
        }, LEAVE_FALLBACK_MS);
      }
    });
    onBeforeUnmount(() => clearTimeout(leaveFallback));
    function onAfterLeave() {
      clearTimeout(leaveFallback);
      steps.value = [];
      direction.value = "forward";
      stepAnimating.value = false;
      emit("didDismiss");
    }
    const sheetRegistry = inject("kontySheetRegistry", null);
    const handle = {
      handleBack() {
        if (leadsBack.value) {
          stepBack();
          return true;
        }
        if (isGuarded.value) return true;
        close();
        return true;
      }
    };
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) sheetRegistry == null ? void 0 : sheetRegistry.register(handle);
      else sheetRegistry == null ? void 0 : sheetRegistry.unregister(handle);
    }, { immediate: true });
    onBeforeUnmount(() => sheetRegistry == null ? void 0 : sheetRegistry.unregister(handle));
    const frameStyle = computed(() => fullscreenActive.value ? {} : { "--cod-sheet-width": props.width, "--cod-sheet-height": props.height });
    const modalUi = computed(() => ({
      ...elevated ? { overlay: Z_SHEET_ELEVATED } : {},
      // Desktop: let the frame drive the size (theme default caps at max-w-lg).
      // Mobile fullscreen: default inset-0 applies; the sheet slides up from the
      // bottom (app-sheet-* keyframes in app-theme; owner 2026-07-10, R58).
      content: (elevated ? `${Z_SHEET_ELEVATED} ` : "") + (fullscreenActive.value ? "data-[state=open]:animate-[app-sheet-in_250ms_ease-out] data-[state=closed]:animate-[app-sheet-out_200ms_ease-in] max-w-none rounded-none" : "w-auto max-w-[calc(100vw-2rem)] max-sm:inset-auto max-sm:left-1/2 max-sm:top-1/2 max-sm:-translate-x-1/2 max-sm:-translate-y-1/2 max-sm:h-auto max-sm:max-h-[calc(100dvh-2rem)] max-sm:w-auto max-sm:max-w-[calc(100vw-2rem)] max-sm:rounded-[var(--radius-frame)]")
    }));
    return (_ctx, _cache) => {
      return mounted.value ? (openBlock(), createBlock(UModal, {
        key: 0,
        open: open.value,
        "onUpdate:open": _cache[5] || (_cache[5] = ($event) => open.value = $event),
        dismissible: false,
        fullscreen: fullscreenActive.value,
        transition: true,
        ui: modalUi.value,
        content: contentHandlers,
        "onAfter:leave": onAfterLeave
      }, {
        content: withCtx(() => [
          createElementVNode("div", {
            class: normalizeClass(["cod-sheet-frame", { "cod-sheet-frame--fullscreen": fullscreenActive.value }]),
            style: normalizeStyle(frameStyle.value)
          }, [
            __props.showHeader ? (openBlock(), createElementBlock("div", _hoisted_1, [
              createElementVNode("h2", _hoisted_2, [
                renderSlot(_ctx.$slots, "title", {}, () => [
                  createTextVNode(toDisplayString(__props.title), 1)
                ], true)
              ]),
              renderSlot(_ctx.$slots, "header-end", {}, void 0, true),
              leadsBack.value || showClose.value ? (openBlock(), createBlock(UButton, {
                key: 0,
                icon: leadsBack.value ? "i-lucide-chevron-left" : "i-lucide-x",
                color: "neutral",
                variant: "ghost",
                size: "md",
                class: normalizeClass([unref(SHEET_LEADING_CONTROL_CLASS), "order-first"]),
                "aria-label": leadsBack.value ? unref(t)("general.back") : unref(t)("general.close"),
                onClick: _cache[0] || (_cache[0] = ($event) => leadsBack.value ? stepBack() : close())
              }, null, 8, ["icon", "class", "aria-label"])) : createCommentVNode("", true)
            ])) : createCommentVNode("", true),
            createElementVNode("div", _hoisted_3, [
              withDirectives(createElementVNode("div", {
                class: normalizeClass(["cod-sheet-step", { "cod-sheet-step--under": depth.value > 0 }])
              }, [
                createVNode(unref(SheetLayer), {
                  nested: false,
                  layer: ROOT_LAYER
                }, {
                  default: withCtx(() => [
                    rootComponent.value ? (openBlock(), createBlock(resolveDynamicComponent(rootComponent.value), normalizeProps(mergeProps({ key: 0 }, __props.rootProps)), null, 16)) : renderSlot(_ctx.$slots, "default", { key: 1 }, void 0, true)
                  ]),
                  _: 3
                })
              ], 2), [
                [vShow, layerShown(0)]
              ]),
              createVNode(TransitionGroup, {
                name: direction.value === "forward" ? "cod-step-next" : "cod-step-prev",
                onAfterEnter: _cache[1] || (_cache[1] = ($event) => stepAnimating.value = false),
                onAfterLeave: _cache[2] || (_cache[2] = ($event) => stepAnimating.value = false),
                onEnterCancelled: _cache[3] || (_cache[3] = ($event) => stepAnimating.value = false),
                onLeaveCancelled: _cache[4] || (_cache[4] = ($event) => stepAnimating.value = false)
              }, {
                default: withCtx(() => [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(steps.value, (step, i) => {
                    return openBlock(), createElementBlock("div", {
                      key: step.id,
                      class: normalizeClass(["cod-sheet-step", { "cod-sheet-step--under": i + 1 < depth.value, "cod-sheet-step--hidden": !layerShown(i + 1) }])
                    }, [
                      createVNode(unref(SheetLayer), {
                        nested: "",
                        layer: step.id
                      }, {
                        default: withCtx(() => [
                          (openBlock(), createBlock(resolveDynamicComponent(step.component), mergeProps({ ref_for: true }, step.props), null, 16))
                        ]),
                        _: 2
                      }, 1032, ["layer"])
                    ], 2);
                  }), 128))
                ]),
                _: 1
              }, 8, ["name"])
            ]),
            _ctx.$slots.footer ? (openBlock(), createElementBlock("div", {
              key: 1,
              class: normalizeClass(["shrink-0", __props.footerChrome ? "flex min-h-17 items-center gap-2 px-4 py-3 *:flex-1" : ""])
            }, [
              renderSlot(_ctx.$slots, "footer", {}, void 0, true)
            ], 2)) : createCommentVNode("", true)
          ], 6)
        ]),
        _: 3
      }, 8, ["open", "fullscreen", "ui"])) : createCommentVNode("", true);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index85.js.map
